import { canViewResource } from './permissions';
import { APP_SLUG_NAV } from './tenantAppNav';
import { SIMPLIMETA_ROOT_TENANT_ID } from './rootTenant';

/**
 * Permission/role-to-route mapping, ordered by priority.
 * Each entry: { path, resource?, permissions?, roles? }
 *   - resource: resource name — matches via canViewResource, which
 *     accepts `${resource}.view`, `.view.all`, or `.view.own`
 *   - permissions: literal permission keys (any match) — use when no
 *     resource convention applies (e.g. 'dashboard.main', 'chat.access')
 *   - roles: role names that grant access regardless of permissions
 * Mirrors the nav items in Layout.js — keep in sync.
 */
const ROUTE_MAP = [
  // Dashboard routes
  { path: '/dashboard', permissions: ['dashboard.main'], roles: ['admin', 'director', 'finance'] },
  { path: '/sales-rep-dashboard', permissions: ['dashboard.sales_rep'], roles: ['sales_rep'] },
  { path: '/my-dashboard', permissions: ['dashboard.agent'], roles: ['agent'] },
  { path: '/contractor-dashboard', permissions: ['dashboard.contractor'], roles: ['contractor'] },
  { path: '/finance', permissions: ['finance.portal'], roles: ['finance'] },
  // Feature pages
  { path: '/deals', resource: 'deals' },
  { path: '/opportunities', resource: 'opportunities' },
  { path: '/ai-employees', permissions: ['ai_employees.view'] },
  { path: '/contacts', resource: 'contacts' },
  { path: '/accounts', resource: 'accounts' },
  { path: '/commissions', resource: 'commissions' },
  { path: '/contracts', resource: 'contracts' },
  { path: '/projects', resource: 'projects' },
  { path: '/time-management', resource: 'time', permissions: ['time.entry'] },
  { path: '/invoices', resource: 'invoices' },
  { path: '/field-jobs', resource: 'field_jobs' },
  { path: '/chat', permissions: ['chat.access'] },
];

/**
 * Determine the best home route for a user based on their permissions and role.
 * Accepts either a role string (legacy) or a user object
 * { role, permissions, effective_permissions }.
 *
 * Prefers `effective_permissions` (the closure-aware list resolved by the
 * backend's implication graph) so a director landing here is matched
 * against routes their `*.edit.all` perms imply view access to. Falls
 * back to `permissions` for older payloads or callers that pass a
 * partial user object.
 */
export default function getRoleHome(roleOrUser) {
  const role = typeof roleOrUser === 'string' ? roleOrUser : roleOrUser?.role;
  const perms = typeof roleOrUser === 'object'
    ? roleOrUser?.effective_permissions || roleOrUser?.permissions || []
    : [];
  const isAdmin = perms.includes('*');

  // chat_only is a special case — always goes to chat
  if (role === 'chat_only') return '/chat';

  // Admin wildcard — go to main dashboard
  if (isAdmin) return '/dashboard';

  // ─── Non-root tenant users: route by GRANTED APPS, not permissions ──
  //
  // The global `tenant_admin` role intentionally over-grants app
  // permission keys (1E.3d auto-seeder unions every granted app's keys
  // onto one shared role), so `finance.portal` et al. are present on a
  // tenant_admin even for tenants that were never granted those apps.
  // Matching ROUTE_MAP by permission here would therefore send a tenant
  // to an internal/ungranted app (e.g. /finance -> middleware 403).
  //
  // Mirror the sidebar (buildTenantNavGroups): a tenant's reachable
  // routes are exactly the apps actively granted to their tenant. Pick
  // the first granted app's route via APP_SLUG_NAV; if the tenant has no
  // granted apps (or none mapped to a route yet), land on the always-on
  // AI Employees spine rather than leaking a permission-matched route.
  // Permission/ROUTE_MAP matching below is reserved for root-tenant
  // (SimpliMeta) users.
  const user = typeof roleOrUser === 'object' ? roleOrUser : null;
  const tenantId = user?.tenant_id;
  const isNonRootTenant = !!tenantId && tenantId !== SIMPLIMETA_ROOT_TENANT_ID;
  if (isNonRootTenant) {
    const grantedSlugs = Array.isArray(user?.granted_app_slugs)
      ? user.granted_app_slugs
      : [];
    for (const slug of grantedSlugs) {
      const route = APP_SLUG_NAV[slug]?.route;
      if (route) return route;
    }
    return '/ai-employees';
  }

  // Find the first route the user has access to (by resource, permission, or role)
  if (perms.length > 0) {
    for (const route of ROUTE_MAP) {
      const byResource = route.resource && canViewResource(perms, route.resource);
      const byPermission =
        route.permissions?.length > 0 && route.permissions.some((p) => perms.includes(p));
      const byRole = route.roles?.includes(role);
      if (byResource || byPermission || byRole) return route.path;
    }
  }

  // Legacy fallback for users without permissions array (role-name only)
  if (role === 'finance') return '/finance';
  if (role === 'contractor') return '/contractor-dashboard';
  if (role === 'sales_rep') return '/sales-rep-dashboard';
  if (role === 'agent') return '/my-dashboard';

  // Ultimate fallback
  return '/dashboard';
}
